import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Collapse,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { ChevronDown, ChevronUp, Search } from "lucide-react";
import GridPage from "components/layout/GridPage";
import Loading from "components/common/Loading";
import Breadcrumbs from "components/common/Breadcrumbs";
import DateDisplay from "components/common/DateDisplay";
import useIsMobile from "components/hooks/useIsMobile";
import { useListFailedAgentThreads } from "../../services/agents";
import {
  ListFailedAgentThreadsRequest,
  ListFailedAgentThreadsResponse_FailedAgentThread,
} from "protogen/agents_service_pb";
import AgentDebugTool, {
  CopyIconButton,
} from "components/tasks/blocks/AgentDebugTool";

const ErrorItem = ({ errorStr }: { errorStr: string }) => {
  const [showTraceback, setShowTraceback] = useState(false);
  let parsed: Record<string, string>;
  try {
    parsed = JSON.parse(errorStr);
  } catch {
    parsed = { error: errorStr };
  }
  return (
    <Box
      sx={{
        p: 1.5,
        backgroundColor: "#FEF2F2",
        border: "1px solid #FECACA",
        borderRadius: "6px",
        fontFamily: "monospace",
        fontSize: "12px",
        lineHeight: 1.5,
        cursor: parsed.traceback ? "pointer" : "default",
      }}
      onClick={() => parsed.traceback && setShowTraceback(!showTraceback)}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <Box>
          {parsed.type && (
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "12px",
                color: "#991B1B",
                mb: 0.5,
              }}
            >
              {parsed.type}
            </Typography>
          )}
          <Typography
            sx={{
              fontSize: "12px",
              color: "#7F1D1D",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {parsed.error || errorStr}
          </Typography>
          {parsed.timestamp && (
            <Typography sx={{ fontSize: "11px", color: "#9CA3AF", mt: 0.5 }}>
              {new Date(parsed.timestamp).toLocaleString()}
            </Typography>
          )}
        </Box>
        {parsed.traceback &&
          (showTraceback ? (
            <ChevronUp size={14} color="#9CA3AF" />
          ) : (
            <ChevronDown size={14} color="#9CA3AF" />
          ))}
      </Box>
      {parsed.traceback && (
        <Collapse in={showTraceback} timeout="auto" unmountOnExit>
          <Box
            sx={{
              mt: 1,
              pt: 1,
              borderTop: "1px solid #FECACA",
              position: "relative",
            }}
          >
            <Box sx={{ position: "absolute", top: 4, right: 0 }}>
              <CopyIconButton
                text={parsed.traceback}
                tooltip="Copy traceback"
              />
            </Box>
            <Typography
              sx={{
                fontSize: "11px",
                color: "#991B1B",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                pr: 4,
              }}
            >
              {parsed.traceback}
            </Typography>
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

const ErrorDetails = ({ errors }: { errors: string[] }) => {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      {errors.map((errorStr, idx) => (
        <ErrorItem key={idx} errorStr={errorStr} />
      ))}
    </Box>
  );
};

const ThreadRow = ({
  thread,
  onDebug,
  defaultExpanded,
}: {
  thread: ListFailedAgentThreadsResponse_FailedAgentThread;
  onDebug: (threadRef: string) => void;
  defaultExpanded?: boolean;
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded ?? false);
  const hasErrors = thread.errors.length > 0;

  return (
    <>
      <TableRow
        hover
        sx={{ cursor: hasErrors ? "pointer" : "default" }}
        onClick={() => hasErrors && setExpanded(!expanded)}
      >
        <TableCell>
          <DateDisplay
            date={new Date(Number(thread.creationSec) * 1000)}
            variant="body"
          />
        </TableCell>
        <TableCell>{thread.entityType}</TableCell>
        <TableCell>{thread.familyName}</TableCell>
        <TableCell>{thread.creatorName}</TableCell>
        <TableCell>{thread.playbookName || "-"}</TableCell>
        <TableCell>{thread.errors.length}</TableCell>
        <TableCell>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Search size={14} />}
            onClick={(e) => {
              e.stopPropagation();
              onDebug(thread.threadRef);
            }}
          >
            Inspect
          </Button>
        </TableCell>
        <TableCell sx={{ width: "32px", pl: 0 }}>
          {hasErrors &&
            (expanded ? (
              <ChevronUp size={16} color="#6B7280" />
            ) : (
              <ChevronDown size={16} color="#6B7280" />
            ))}
        </TableCell>
      </TableRow>
      {hasErrors && (
        <TableRow>
          <TableCell
            colSpan={8}
            sx={{ py: 0, borderBottom: expanded ? undefined : "none" }}
          >
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Box sx={{ py: 2 }}>
                <ErrorDetails errors={thread.errors} />
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

export default () => {
  const isMobile = useIsMobile();
  const [searchParams] = useSearchParams();
  const threadRefParam = searchParams.get("threadRef");
  const { request, loading } = useListFailedAgentThreads();
  const [threads, setThreads] = useState<
    ListFailedAgentThreadsResponse_FailedAgentThread[]
  >([]);
  const [nextCursor, setNextCursor] = useState<bigint>(BigInt(0));
  const [debugThreadRef, setDebugThreadRef] = useState<string | null>(null);

  const fetchPage = async (cursor?: bigint) => {
    const req = new ListFailedAgentThreadsRequest({
      pageSize: 25,
      ...(threadRefParam ? { threadRef: threadRefParam } : {}),
    });
    if (cursor) {
      req.cursor = cursor;
    }
    const resp = await request(req);
    if (resp) {
      if (cursor) {
        setThreads((prev) => [...prev, ...resp.threads]);
      } else {
        setThreads(resp.threads);
      }
      setNextCursor(resp.nextCursor);
    }
  };

  useEffect(() => {
    fetchPage();
  }, []);

  return (
    <GridPage
      sx={{
        padding: isMobile ? "32px 24px" : "48px",
        margin: "auto",
        maxWidth: "1200px",
      }}
    >
      <Box display="flex" flexDirection="column" gap="16px">
        <Breadcrumbs
          breadcrumbs={[
            { name: "Home", link: "/" },
            { name: "Tools", link: "/tools" },
          ]}
        />
        <Typography variant="display">Agent Errors</Typography>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Family</TableCell>
                <TableCell>Creator</TableCell>
                <TableCell>Playbook</TableCell>
                <TableCell>Errors</TableCell>
                <TableCell />
                <TableCell sx={{ width: "32px" }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {threads.map((thread) => (
                <ThreadRow
                  key={thread.threadRef}
                  thread={thread}
                  onDebug={setDebugThreadRef}
                  defaultExpanded={threads.length === 1}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {loading && <Loading />}
        {!loading && threads.length === 0 && (
          <Typography variant="body" color="textSecondary" textAlign="center">
            No failed agent threads found.
          </Typography>
        )}
        {nextCursor > 0 && !loading && (
          <Box display="flex" justifyContent="center">
            <Button
              variant="outlined"
              onClick={() => fetchPage(nextCursor)}
              disabled={loading}
            >
              Load more
            </Button>
          </Box>
        )}
      </Box>
      {debugThreadRef && (
        <AgentDebugTool
          threadRef={debugThreadRef}
          open={!!debugThreadRef}
          onClose={() => setDebugThreadRef(null)}
        />
      )}
    </GridPage>
  );
};
